import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  Text,
  Spinner,
  Badge,
  Progress,
  HStack,
} from "@chakra-ui/react";
import ReactPlayer from "react-player";
import { FaMagnifyingGlass, FaDownload } from "react-icons/fa6";
import { formatDuration } from "../utilities/timestamps";
import { useParams } from "react-router-dom";
import { useGetVideoPreviewsQuery, useGetSceneResultQuery } from "services/api";
import {
  useGetProcessingStatusQuery,
  useConvertSceneToVideoMutation,
} from "services/api/v2/captainNapalm";
import { useGenerateVideoMutation } from "services/api/v2/videos";
import { API_URL } from "constants";
import { useToast } from "@chakra-ui/react";

export default function Scene({
  onSelect,
  sceneId,
  projectId,
  isPlaceholder = false,
  jobId = null,
  onSceneComplete,
}) {
  const { sceneId: selectedSceneId } = useParams();
  const toast = useToast();
  const [loadingMP4, setLoadingMP4] = useState(false);

  // For placeholder scenes, poll for status updates
  const { data: statusData } = useGetProcessingStatusQuery(
    { token: projectId, jobId },
    {
      pollingInterval: isPlaceholder && jobId ? 2000 : 0,
      skip: !isPlaceholder || !jobId,
    }
  );

  // For regular scenes, fetch scene data
  const { data: scene, isLoading, error } = useGetSceneResultQuery(
    { token: projectId, sceneName: sceneId },
    { skip: isPlaceholder || !sceneId }
  );

  // Video conversion and generation mutations
  const [convertSceneToVideo] = useConvertSceneToVideoMutation();
  const [generateVideo] = useGenerateVideoMutation();

  // Fetch video previews to get the video URL
  const { data: previewsData } = useGetVideoPreviewsQuery(projectId, {
    skip: !projectId,
  });

  // Helper function to get video URL from previews
  const getVideoUrl = () => {
    if (!previewsData?.previews || !scene?.scene_name) return null;

    const matchingPreview = previewsData.previews.find(
      (preview) => preview.scene_name === scene.scene_name
    );

    if (matchingPreview?.preview_url) {
      // Construct the full URL by combining API_URL with the preview_url
      return `${API_URL}${matchingPreview.preview_url}`;
    }

    return null;
  };

  const videoUrl = getVideoUrl();

  // Handle MP4 download
  const handleDownloadMP4 = async () => {
    console.log("Downloading MP4 for scene:", scene);
    const videoUrl = getVideoUrl();

    if (!videoUrl) {
      toast({
        title: "Download failed",
        description: "No video available for download",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    setLoadingMP4(true);
    try {
      // Fetch the video file from the video URL
      const response = await fetch(videoUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the video as a blob
      const videoBlob = await response.blob();

      // Create a URL for the blob
      const url = URL.createObjectURL(videoBlob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = (scene?.title || scene?.scene_name || "scene") + ".mp4";

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      URL.revokeObjectURL(url);

      toast({
        title: "Download successful",
        description: `${
          scene?.title || scene?.scene_name
        } MP4 downloaded successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      console.error("MP4 download error:", error);

      toast({
        title: "Download failed",
        description: `Failed to download MP4: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setLoadingMP4(false);
    }
  };

  // Handle scene completion workflow
  useEffect(() => {
    const handleSceneCompletion = async () => {
      if (
        isPlaceholder &&
        statusData?.status === "SUCCESS" &&
        onSceneComplete
      ) {
        try {
          // 1. Convert scene to video
          const convertResult = await convertSceneToVideo({
            token: projectId,
            sceneName: statusData.scene_info?.scene_name || sceneId,
          }).unwrap();

          // 2. Generate video from the converted scene using the response data
          if (convertResult.success && convertResult.transmogrifier_data) {
            const { transmogrifier_data } = convertResult;

            // Construct the clips array from the transmogrifier data
            const clips = transmogrifier_data.clips.map((clip) => ({
              fileURL: clip.fileURL,
              filename: clip.filename,
              target_duration: clip.target_duration,
              shot_name: clip.shot_name,
              start: clip.start.toString(),
              end: clip.end.toString(),
            }));

            // Generate video with the proper request structure
            await generateVideo({
              token: projectId,
              jobData: {
                job_name: transmogrifier_data.job_name,
                sequence_name: transmogrifier_data.sequence_name,
                description: transmogrifier_data.description,
                clips: clips,
              },
            }).unwrap();
          }

          // 3. Notify parent component to handle completion
          onSceneComplete(sceneId);
        } catch (error) {
          console.error("Error in scene completion workflow:", error);
        }
      }
    };

    handleSceneCompletion();
  }, [
    isPlaceholder,
    statusData?.status,
    projectId,
    sceneId,
    convertSceneToVideo,
    generateVideo,
    onSceneComplete,
  ]);

  // For placeholder scenes, use status data
  if (isPlaceholder) {
    const progress = statusData?.progress ?? 0;
    const statusMessage = statusData?.status_message ?? "Initializing...";
    const status = statusData?.status ?? "PENDING";
    const sceneName = statusData?.scene_info?.scene_name ?? sceneId;

    const getStatusColor = () => {
      switch (status) {
        case "SUCCESS":
        case "COMPLETED":
          return "green";
        case "FAILED":
          return "red";
        case "PROCESSING":
          return "blue";
        default:
          return "gray";
      }
    };

    const getStatusText = () => {
      switch (status) {
        case "SUCCESS":
          return "Converting to video...";
        case "COMPLETED":
          return "Generation complete";
        case "FAILED":
          return "Generation failed";
        case "PROCESSING":
          return "Generation in progress";
        default:
          return "Initializing...";
      }
    };

    const statusColor = getStatusColor();
    const statusText = getStatusText();

    return (
      <Box
        borderColor="blue.200"
        borderRadius="md"
        borderWidth="1px"
        boxShadow="0 0 0 1px rgba(66, 153, 225, 0.3)"
        p={3}
        position="relative"
      >
        <IconButton
          _hover={{ bg: "blue.50" }}
          aria-label="View scene details"
          bg="white"
          boxShadow="0 0 4px rgba(0,0,0,0.2)"
          colorScheme="blue"
          disabled={true}
          icon={<FaMagnifyingGlass />}
          position="absolute"
          right="8px"
          size="sm"
          top="8px"
          variant="ghost"
          zIndex={2}
        />

        <Flex
          alignItems="center"
          bg="gray.50"
          borderRadius="md"
          direction="column"
          height="100px"
          justifyContent="center"
          mb={2}
          position="relative"
          width="100%"
        >
          <Spinner color="blue.500" size="md" thickness="3px" />
          <Text color="blue.500" fontSize="xs" fontWeight="medium" mt={2}>
            {statusText}
          </Text>
        </Flex>

        <Heading as="h3" fontSize="md" mb={1} mt={2}>
          {sceneName}
        </Heading>

        <Flex alignItems="center" borderRadius="sm" mb={1}>
          <Text color="blue.500" fontSize="xs" fontWeight="medium">
            {statusText}
          </Text>
        </Flex>

        {/* Progress Bar */}
        <Box mb={2}>
          <Flex justifyContent="space-between" mb={1}>
            <Text color="gray.600" fontSize="xs">
              Progress
            </Text>
            <Text color="gray.600" fontSize="xs">
              {progress}%
            </Text>
          </Flex>
          <Progress
            colorScheme={statusColor}
            hasStripe={status === "PROCESSING"}
            isAnimated={status === "PROCESSING"}
            size="sm"
            value={progress}
          />
        </Box>

        {/* Status Message */}
        <Text color="gray.600" fontSize="xs" noOfLines={2}>
          {statusMessage}
        </Text>
      </Box>
    );
  }

  // Show loading state while fetching scene data
  if (isLoading) {
    return (
      <Box
        borderColor="gray.200"
        borderRadius="md"
        borderWidth="1px"
        p={3}
        position="relative"
      >
        <Flex
          alignItems="center"
          bg="gray.50"
          borderRadius="md"
          direction="column"
          height="100px"
          justifyContent="center"
          mb={2}
          position="relative"
          width="100%"
        >
          <Spinner color="blue.500" size="md" thickness="3px" />
          <Text color="blue.500" fontSize="xs" fontWeight="medium" mt={2}>
            Loading scene...
          </Text>
        </Flex>
      </Box>
    );
  }

  // Show error state if scene fetch failed
  if (error || !scene) {
    return (
      <Box
        borderColor="red.200"
        borderRadius="md"
        borderWidth="1px"
        p={3}
        position="relative"
      >
        <Text color="red.500" fontSize="sm">
          Failed to load scene
        </Text>
      </Box>
    );
  }

  // Regular scene rendering (existing code)
  const isSelected = selectedSceneId === scene.scene_name;
  const isGenerating = scene.status === "generating";
  const isDraft = scene.status === "draft";
  const sceneTitle = scene.title || scene.scene_name;
  const scenePrompt = scene.scene_data?.description || "No prompt available";
  const sceneIdValue = scene.scene_name;

  return (
    <Box
      borderColor={
        isSelected
          ? "blue.500"
          : isGenerating
          ? "blue.200"
          : isDraft
          ? "orange.200"
          : "gray.200"
      }
      borderRadius="md"
      borderWidth="1px"
      boxShadow={
        isSelected
          ? "0 0 0 2px rgba(66, 153, 225, 0.6)"
          : isGenerating
          ? "0 0 0 1px rgba(66, 153, 225, 0.3)"
          : isDraft
          ? "0 0 0 1px rgba(237, 137, 54, 0.3)"
          : "none"
      }
      p={3}
      position="relative"
    >
      <IconButton
        _hover={{ bg: "blue.50" }}
        aria-label="View scene details"
        bg="white"
        boxShadow="0 0 4px rgba(0,0,0,0.2)"
        colorScheme="blue"
        disabled={isSelected || isGenerating}
        icon={<FaMagnifyingGlass />}
        onClick={() => onSelect(scene.scene_name)}
        position="absolute"
        right="8px"
        size="sm"
        top="8px"
        variant={isSelected ? "solid" : "ghost"}
        zIndex={2}
      />

      {isGenerating ? (
        <Flex
          alignItems="center"
          bg="gray.50"
          borderRadius="md"
          direction="column"
          height="100px"
          justifyContent="center"
          mb={2}
          position="relative"
          width="100%"
        >
          <Spinner color="blue.500" size="md" thickness="3px" />
          <Text color="blue.500" fontSize="xs" fontWeight="medium" mt={2}>
            Generating scene...
          </Text>
        </Flex>
      ) : (
        <Box margin="0 auto" maxWidth="320px" position="relative" width="100%">
          <Box position="relative" width="100%">
            {videoUrl ? (
              <ReactPlayer
                controls
                height="300px"
                url={videoUrl}
                width="100%"
              />
            ) : (
              <Box
                alignItems="center"
                bg="gray.100"
                borderRadius="md"
                display="flex"
                height="100%"
                justifyContent="center"
                width="100%"
              >
                <Text color="gray.500" fontSize="sm">
                  No video available
                </Text>
              </Box>
            )}
            {!!isDraft && (
              <Badge
                colorScheme="orange"
                left="8px"
                position="absolute"
                top="8px"
                zIndex="1"
              >
                Draft
              </Badge>
            )}
          </Box>
        </Box>
      )}

      <Heading as="h3" fontSize="md" mb={1} mt={2}>
        {sceneTitle}
      </Heading>

      {isGenerating ? (
        <Flex alignItems="center" borderRadius="sm" mb={1}>
          <Text color="blue.500" fontSize="xs" fontWeight="medium">
            Generation in progress
          </Text>
        </Flex>
      ) : isDraft ? (
        <Flex alignItems="center" borderRadius="sm" mb={1}>
          <Text color="orange.500" fontSize="xs" fontWeight="medium">
            Draft scene
          </Text>
        </Flex>
      ) : (
        <Flex alignItems="center" mb={1}>
          <Text color="gray.500" fontSize="xs" mr={1}>
            ID: {sceneIdValue}
          </Text>
          {scene?.duration ? (
            <Text color="gray.500" fontSize="xs">
              Duration: {formatDuration(scene.duration)}
            </Text>
          ) : null}
        </Flex>
      )}

      <Text color="gray.600" fontSize="sm" noOfLines={2}>
        {scenePrompt}
      </Text>

      {/* Download MP4 Button */}
      {!isGenerating && videoUrl && (
        <HStack mt={3}>
          <IconButton
            aria-label="Download MP4"
            borderRadius="md"
            colorScheme="blue"
            icon={
              loadingMP4 ? (
                <Spinner size="xs" />
              ) : (
                <>
                  <FaDownload size="10px" />{" "}
                  <Text fontSize="xs" ml={1}>
                    MP4
                  </Text>
                </>
              )
            }
            isLoading={loadingMP4}
            onClick={handleDownloadMP4}
            pl={2}
            pr={2}
            size="xs"
            title="Download MP4"
          />
        </HStack>
      )}
    </Box>
  );
}
